import React, { useEffect, useState } from "react";

import DeleteIcon from "@mui/icons-material/DeleteOutlined";
import DownloadIcon from "@mui/icons-material/Download";
import InfoIcon from "@mui/icons-material/Info";
import EditIcon from "@mui/icons-material/Edit";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";

import { GridActionsCellItem } from "@mui/x-data-grid";
import { CustomPagination } from "../../customPagination";
import { ConvertISOToDate, GetColorFromState } from "../../util";
import { canUserEditOrDelete, getOperationPriority, getWaypointsDownloadable } from "./opUtil";
import { handleDownloadOperations, userAccountHasFirstAndLastName } from "./opUtil";
import { FlightInfoDialog } from "./flightInfoDialog";

import { useUserAuth } from "../../contexts/authContext";
import { useMap } from "../../contexts/mapContext";

import MapPreview from "../../map/mapPreview";
import ExportFlightWaypointsDialog from "./exportFlightWaypointsDialog";
import FlightCreator from "./flightCreator";

import * as CONSTANT from "./opConstants";
import ResponsiveGrid from "../../grid/responsiveGrid";

export function PlanningOps() {
    const [showMap, setShowMap] = useState(false);
    const [flightCreatorOpen, setFlightCreatorOpen] = useState(false);
    const [selectedFlightUUID, setSelectedFlightUUID] = useState(null);
    const [selectedPlanningOperations, setSelectedPlanningOperations] = useState([]);
    const [operationsAreSelected, setOperationsAreSelected] = useState(false);

    const [downloadWaypointDialogOpen, setDownloadWaypointDialogOpen] = useState(false);
    const [downloadWaypointOperation, setDownloadWaypointOperation] = useState(null);

    const [infoFlight, setInfoFlight] = useState(null);
    const [flightInfoOpen, setFlightInfoOpen] = useState(false);

    const { planningOperations, drones, getDrones } = useMap();
    const { user, userMapSettings, getOrganizationByID, handleFailedFetch } = useUserAuth();

    const laancStates = [
        { state: CONSTANT.PLANNING_STATUS, color: "#E0D268" },
        { state: CONSTANT.LAANC_FURTHER_COORD_STATUS, color: "#E0D268" },
        { state: CONSTANT.DECONFLICTION_BLOCKED_STATUS, color: "#CB4C4E" },
        { state: CONSTANT.LAANC_BLOCKED_STATUS, color: "#CB4C4E" },
        { state: CONSTANT.LAANC_NOT_REQUIRED_STATUS, color: "#5fa052" },
        { state: CONSTANT.LAANC_AUTO_APPROVED_STATUS, color: "#5fa052" },
        { state: CONSTANT.FAA_APPROVAL_BLOCKED_STATUS, color: "#CB4C4E" },
        { state: CONSTANT.FAA_APPROVAL_CLEAR_STATUS, color: "#5fa052" }
    ];

    const dateTimeColumns = [
        {
            field: "info",
            headerName: "",
            minWidth: 50,
            maxWidth: 50,
            flex: 1,
            display: "flex",
            renderCell: (cellValues) => (
                <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", width: "100%" }}>
                    <IconButton size="medium" onClick={(event) => handleInfoClick(event, cellValues)} id={`info-${cellValues.row.volumeId}`} sx={{ p: 0 }}>
                        <InfoIcon />
                    </IconButton>
                </Box>
            )
        },
        {
            field: "name",
            headerName: "Name",
            flex: 1,
            editable: false,
            minWidth: 175
        },
        {
            field: "time_start",
            headerName: "Start Date",
            editable: false,
            type: "dateTime",
            minWidth: 225,
            flex: 1,
            valueFormatter: (value) => `${ConvertISOToDate(value)}`
        },
        {
            field: "time_end",
            headerName: "End Date",
            editable: false,
            type: "dateTime",
            minWidth: 225,
            flex: 1,
            valueFormatter: (value) => `${ConvertISOToDate(value)}`
        },
        {
            field: "organization_id",
            headerName: "Org",
            editable: false,
            minWidth: 75,
            maxWidth: 75,
            flex: 1,
            renderCell: ({ formattedValue }) => {
                const org = getOrganizationByID(formattedValue);
                return org ? <>{org.name}</> : <></>;
            }
        },
        {
            field: "pilot_uuid",
            headerName: "Pilot",
            editable: false,
            minWidth: 175,
            flex: 1,
            valueGetter: (value, row) => `${row.pilot_name || row.pilot_email}`
        },
        {
            field: "vehicle_uuid",
            headerName: "Vehicle",
            editable: false,
            minWidth: 100,
            flex: 1,
            renderCell: ({ formattedValue }) => {
                const drone = drones.find((drone) => drone.vehicle_uuid === formattedValue);
                return drone ? drone.name : "Unknown";
            }
        },
        {
            field: "state",
            headerName: "Status",
            editable: false,
            minWidth: 250,
            flex: 1,
            renderCell: (cellValues) => {
                let fontColor = GetColorFromState(cellValues.formattedValue, laancStates);
                return (
                    <div style={{ fontWeight: "bold", color: fontColor }} id={`status-${cellValues.row.volumeId}`}>
                        {cellValues.formattedValue.toUpperCase().replace(/_/g, " ")}
                    </div>
                );
            }
        },
        {
            field: "priority",
            headerName: "Priority",
            minWidth: 125,
            flex: 1,
            editable: false,
            valueGetter: (value, row) => `${getOperationPriority(row)}`
        },
        {
            type: "actions",
            field: "actions",
            minWidth: 125,
            headerName: "Actions",
            cellClassName: "actions",
            getActions: ({ row }) => {
                const canEdit = canUserEditOrDelete(row, user);
                const canDelete = canUserEditOrDelete(row, user);
                const canDownloadWaypoints = getWaypointsDownloadable(row, user);
                const actions = [];
                if (canEdit) {
                    actions.push(
                        <GridActionsCellItem
                            key={row.id}
                            icon={<EditIcon />}
                            label="Edit"
                            id={`edit-${row.volumeId}`}
                            onClick={() => {
                                setSelectedFlightUUID(row.flight_uuid);
                                setFlightCreatorOpen(true);
                            }}
                        />
                    );
                }
                if (canDownloadWaypoints) {
                    actions.push(
                        <GridActionsCellItem
                            key={row.id}
                            icon={<DownloadIcon />}
                            label="Download"
                            onClick={() => {
                                setDownloadWaypointDialogOpen(true);
                                setDownloadWaypointOperation(row);
                            }}
                        />
                    );
                }
                if (canDelete) {
                    actions.push(
                        <GridActionsCellItem
                            key={row.id}
                            icon={<DeleteIcon />}
                            label="Delete"
                            id={`delete-${row.volumeId}`}
                            onClick={() => {
                                handleDeleteClick(row);
                            }}
                        />
                    );
                }
                return actions;
            }
        }
    ];

    useEffect(() => {
        if (operationsAreSelected === false) {
            setSelectedPlanningOperations(planningOperations);
        }
        getDrones();
    }, [planningOperations, selectedPlanningOperations, operationsAreSelected]);

    const handleDeleteClick = (operation) => {
        if (confirm("Are you sure you want to delete this operation from planning?") === false) {
            return;
        }
        operation.updated_user_id = user.id;
        operation.state = "DELETED";

        const requestOptions = {
            method: "PUT",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(operation)
        };
        fetch("/api/op/deconflict", requestOptions)
            .then((response) => response.json())
            .catch((err) => handleFailedFetch(err));
    };

    const handleCheckboxSelected = (ids) => {
        if (ids.length > 0 && showMap === false) {
            setShowMap(true);
        }
        const selected = planningOperations.filter(({ flight_uuid }) => {
            return ids.includes(flight_uuid);
        });
        if (selected.length > 0) {
            setOperationsAreSelected(true);
            setSelectedPlanningOperations(selected);
        } else {
            setOperationsAreSelected(false);
            setSelectedPlanningOperations(planningOperations);
        }
    };

    const handleOpenCreateDialog = () => {
        if (userAccountHasFirstAndLastName(user)) {
            setSelectedFlightUUID(null);
            setFlightCreatorOpen(true);
        } else {
            alert("Please include a first and last name in your account settings before creating an operation");
        }
    };

    const handleInfoClick = (event, cellValues) => {
        event.stopPropagation();
        setInfoFlight(cellValues.row);
        setFlightInfoOpen(true);
    };

    return flightCreatorOpen ? (
        <FlightCreator setFlightCreatorOpen={setFlightCreatorOpen} flightUUID={selectedFlightUUID} publishedFlight={false} />
    ) : (
        <Box sx={{ p: 1, width: "100%", height: "100%", display: "flex", flexDirection: "column", gap: 1, overflowY: "auto" }}>
            <Typography component="h2" variant="h6" color="#6b778c" noWrap sx={{ flexShrink: 0, p: { xs: 1, sm: 2 }, pb: 0 }}>
                Operation Planning
            </Typography>

            <Box
                sx={{
                    flex: 1,
                    width: "100%",
                    display: "flex",
                    flexDirection: { xs: "column-reverse", sm: "column" },
                    justifyContent: { xs: "flex-end", sm: "flex-start" }
                }}
            >
                <Paper
                    sx={{
                        p: { xs: 1, sm: 2 },
                        display: "flex",
                        flexDirection: { xs: "column-reverse", sm: "column" },
                        width: "100%",
                        gap: { xs: 1, sm: 2 },
                        height: { xs: "100%", sm: "710px" },
                        flexShrink: { xs: "unset", sm: 0 }
                    }}
                >
                    <Box sx={{ height: "100%", width: "100%", display: "flex", flexDirection: { xs: "column-reverse", sm: "column" }, gap: 2 }}>
                        <Box sx={{ position: "relative", flex: 1, display: "flex", flexDirection: "column", gap: 2 }}>
                            <ResponsiveGrid
                                primaryColumns={["name", "time_start", "actions"]}
                                checkboxSelection
                                rows={planningOperations}
                                disableVirtualization
                                disableRowSelectionOnClick
                                columns={dateTimeColumns}
                                getRowId={(row) => row.flight_uuid}
                                pagination
                                slots={{ pagination: CustomPagination }}
                                onRowSelectionModelChange={handleCheckboxSelected}
                                sx={{ position: "absolute", top: 0, right: 0, bottom: 0, left: 0 }}
                            />
                        </Box>
                        <Box sx={{ display: "flex", flexDirection: { xs: "row-reverse", sm: "row" }, gap: 1, overflow: "auto", flexShrink: 0 }}>
                            <Button
                                onClick={() => setShowMap((prev) => !prev)}
                                variant="contained"
                                color="primary"
                                data-testid="preview"
                                size="small"
                                sx={{ whiteSpace: "nowrap", flexShrink: 0 }}
                            >
                                {showMap ? "Hide Map" : "Preview on Map"}
                            </Button>
                            <Button
                                variant="contained"
                                color="primary"
                                onClick={() => handleDownloadOperations(selectedPlanningOperations, userMapSettings)}
                                data-testid="download"
                                size="small"
                                sx={{ mr: "auto", whiteSpace: "nowrap", flexShrink: 0 }}
                                id="download"
                            >
                                Download KML
                            </Button>
                            <Button
                                variant="contained"
                                color="primary"
                                size="small"
                                id="createOperation"
                                data-testid="create"
                                onClick={handleOpenCreateDialog}
                                sx={{ whiteSpace: "nowrap", flexShrink: 0 }}
                            >
                                Create Operation
                            </Button>
                        </Box>
                    </Box>
                </Paper>

                {showMap ? (
                    <Paper sx={{ flexShrink: 0, height: { xs: "40%", sm: "400px" }, px: { xs: 1, sm: 2 }, pt: { xs: 1, sm: 0 }, pb: { xs: 0, sm: 2 } }}>
                        <MapPreview selected_planned_flights={selectedPlanningOperations} />
                    </Paper>
                ) : (
                    <></>
                )}
                {downloadWaypointDialogOpen === true ? (
                    <ExportFlightWaypointsDialog
                        downloadWaypointDialogOpen={downloadWaypointDialogOpen}
                        setDownloadWaypointDialogOpen={setDownloadWaypointDialogOpen}
                        downloadWaypointOperation={downloadWaypointOperation}
                        setDownloadWaypointOperation={setDownloadWaypointOperation}
                    />
                ) : (
                    <></>
                )}
                {flightInfoOpen ? <FlightInfoDialog flight={infoFlight} flightInfoOpen={flightInfoOpen} setFlightInfoOpen={setFlightInfoOpen} /> : <></>}
            </Box>
        </Box>
    );
}
